import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowLeft, Printer, TrendingUp, TrendingDown } from 'lucide-react';
import BlurredValue from '../../../components/common/BlurredValue';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const WholesaleIncomeStatementPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [period, setPeriod] = useState('this_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [compareEnabled, setCompareEnabled] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [period]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken');

      let url;
      if (period === 'custom') {
        if (!customStartDate || !customEndDate) {
          setError('Please select both start and end dates for custom range');
          setLoading(false);
          return;
        }
        url = `${API_URL}/reports/income-statement?start_date=${customStartDate}&end_date=${customEndDate}&compare=${compareEnabled}&module=wholesale`;
      } else {
        url = `${API_URL}/reports/income-statement/${period}?module=wholesale`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setReportData(response.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch wholesale income statement');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const renderVariance = (variance) => {
    if (!variance) return null;

    const isPositive = variance.amount >= 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';

    return (
      <div className={`flex items-center gap-1 ${colorClass} text-sm`}>
        <Icon className="h-4 w-4" />
        <span>{formatCurrency(Math.abs(variance.amount))}</span>
        <span>({formatPercentage(variance.percentage)})</span>
      </div>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 print:hidden">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/wholesale/reports')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wholesale Income Statement</h1>
              <p className="text-sm text-gray-500">Profit & Loss Report - Wholesale Module</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="text-white px-4 py-2 rounded-lg flex items-center gap-2"
            style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="this_year">This Year</option>
                <option value="last_month">Last Month</option>
                <option value="last_quarter">Last Quarter</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </>
            )}

            <div className="flex items-end">
              <button
                onClick={fetchReport}
                className="w-full text-white px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
              >
                Generate Report
              </button>
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={compareEnabled}
                onChange={(e) => setCompareEnabled(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Compare with Previous Period</span>
            </label>
          </div>
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-6">
          <h1 className="text-2xl font-bold text-gray-900 text-center">
            Wholesale Income Statement (Profit & Loss)
          </h1>
          <p className="text-sm text-gray-600 text-center">{reportData?.period?.label}</p>
          <p className="text-sm text-gray-600 text-center">Wholesale Module Report</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 print:hidden">
            {error}
          </div>
        )}

        {reportData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 print:hidden">
              <div className="bg-white rounded-lg shadow p-4 border-2 border-emerald-500">
                <div className="text-sm text-gray-500">Total Revenue</div>
                <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                  <BlurredValue value={formatCurrency(reportData.current.revenue.total)} />
                </div>
                {reportData.variance && renderVariance(reportData.variance.revenue)}
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-500">
                <div className="text-sm text-gray-500">
                  {reportData.current.gross_profit >= 0 ? 'Gross Profit' : 'Gross Loss'}
                </div>
                <div
                  className={`text-xl md:text-2xl lg:text-3xl font-bold ${reportData.current.gross_profit >= 0 ? 'text-gray-900' : 'text-red-600'}`}
                >
                  <BlurredValue value={formatCurrency(reportData.current.gross_profit)} />
                </div>
                {reportData.variance && renderVariance(reportData.variance.gross_profit)}
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-purple-500">
                <div className="text-sm text-gray-500">
                  {reportData.current.operating_profit >= 0 ? 'Operating Profit' : 'Operating Loss'}
                </div>
                <div
                  className={`text-xl md:text-2xl lg:text-3xl font-bold ${reportData.current.operating_profit >= 0 ? 'text-gray-900' : 'text-red-600'}`}
                >
                  <BlurredValue value={formatCurrency(reportData.current.operating_profit)} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4 border-2 border-amber-500">
                <div className="text-sm text-gray-500">
                  {reportData.current.net_profit_after_tax >= 0 ? 'Net Profit' : 'Net Loss'}
                </div>
                <div
                  className={`text-xl md:text-2xl lg:text-3xl font-bold ${reportData.current.net_profit_after_tax >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                >
                  <BlurredValue value={formatCurrency(reportData.current.net_profit_after_tax)} />
                </div>
                {reportData.variance && renderVariance(reportData.variance.net_profit)}
              </div>
            </div>

            {/* Detailed Report */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Account
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      {reportData.comparison && (
                        <>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Previous Period
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            Variance
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Revenue Section */}
                    <tr className="bg-emerald-50">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-3 text-sm font-bold text-gray-900"
                      >
                        REVENUE
                      </td>
                    </tr>
                    {reportData.current.revenue.by_category.map((cat, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-sm text-gray-900 pl-10">
                          {cat.category_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-900">
                          {formatCurrency(cat.amount)}
                        </td>
                        {reportData.comparison && (
                          <>
                            <td className="px-6 py-4 text-sm text-right text-gray-500">
                              {formatCurrency(
                                reportData.comparison.revenue.by_category[idx]?.amount || 0
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-right"></td>
                          </>
                        )}
                      </tr>
                    ))}
                    <tr className="font-bold">
                      <td className="px-6 py-4 text-sm text-gray-900">Total Revenue</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">
                        <BlurredValue value={formatCurrency(reportData.current.revenue.total)} />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.revenue.total)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right">
                            {renderVariance(reportData.variance.revenue)}
                          </td>
                        </>
                      )}
                    </tr>

                    {/* COGS Section */}
                    <tr className="bg-gray-100">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-3 text-sm font-bold text-gray-900"
                      >
                        COST OF GOODS SOLD
                      </td>
                    </tr>
                    {/* Product Cost */}
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 pl-10">Product Cost</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">
                        ({formatCurrency(reportData.current.cogs.product_cogs || 0)})
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-sm text-right text-gray-500">
                            ({formatCurrency(reportData.comparison.cogs.product_cogs || 0)})
                          </td>
                          <td className="px-6 py-4 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    {/* Order-Linked Expenses */}
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 pl-10">
                        Order-Linked Expenses
                      </td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">
                        ({formatCurrency(reportData.current.cogs.expense_cogs || 0)})
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-sm text-right text-gray-500">
                            ({formatCurrency(reportData.comparison.cogs.expense_cogs || 0)})
                          </td>
                          <td className="px-6 py-4 text-sm text-right"></td>
                        </>
                      )}
                    </tr>
                    {/* Total COGS */}
                    <tr className="font-bold">
                      <td className="px-6 py-4 text-sm text-gray-900">Total Cost of Goods Sold</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">
                        ({formatCurrency(reportData.current.cogs.total)})
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-sm text-right text-gray-500">
                            ({formatCurrency(reportData.comparison.cogs.total)})
                          </td>
                          <td className="px-6 py-4 text-sm text-right">
                            {renderVariance(reportData.variance.cogs)}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr className="font-bold bg-emerald-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {reportData.current.gross_profit >= 0 ? 'Gross Profit' : 'Gross Loss'}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm text-right ${reportData.current.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                      >
                        <BlurredValue value={formatCurrency(reportData.current.gross_profit)} />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-sm text-right text-gray-500">
                            {formatCurrency(reportData.comparison.gross_profit)}
                          </td>
                          <td className="px-6 py-4 text-sm text-right">
                            {renderVariance(reportData.variance.gross_profit)}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-2 text-xs text-gray-500 italic pl-10">
                        {reportData.current.gross_profit >= 0
                          ? 'Gross Profit Margin'
                          : 'Gross Loss Margin'}
                        : {reportData.current.gross_profit_margin.toFixed(2)}%
                      </td>
                      <td colSpan={reportData.comparison ? 3 : 1}></td>
                    </tr>

                    {/* Operating Expenses */}
                    <tr className="bg-gray-100">
                      <td
                        colSpan={reportData.comparison ? 4 : 2}
                        className="px-6 py-3 text-sm font-bold text-gray-900"
                      >
                        OPERATING EXPENSES
                      </td>
                    </tr>
                    {reportData.current.operating_expenses.breakdown.map((exp, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-sm text-gray-900 pl-10">
                          {exp.account_name}
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-900">
                          ({formatCurrency(exp.amount)})
                        </td>
                        {reportData.comparison && (
                          <>
                            <td className="px-6 py-4 text-sm text-right text-gray-500"></td>
                            <td className="px-6 py-4 text-sm text-right"></td>
                          </>
                        )}
                      </tr>
                    ))}
                    <tr className="font-bold">
                      <td className="px-6 py-4 text-sm text-gray-900">Total Operating Expenses</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">
                        ({formatCurrency(reportData.current.operating_expenses.total)})
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-sm text-right text-gray-500">
                            ({formatCurrency(reportData.comparison.operating_expenses.total)})
                          </td>
                          <td className="px-6 py-4 text-sm text-right">
                            {renderVariance(reportData.variance.operating_expenses)}
                          </td>
                        </>
                      )}
                    </tr>

                    {/* Net Profit */}
                    <tr className="font-bold bg-emerald-50 border-t-2 border-gray-300">
                      <td className="px-6 py-4 text-base text-gray-900">
                        {reportData.current.net_profit_after_tax >= 0 ? 'NET PROFIT' : 'NET LOSS'}
                      </td>
                      <td
                        className={`px-6 py-4 text-base text-right ${reportData.current.net_profit_after_tax >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                      >
                        <BlurredValue
                          value={formatCurrency(reportData.current.net_profit_after_tax)}
                        />
                      </td>
                      {reportData.comparison && (
                        <>
                          <td className="px-6 py-4 text-base text-right text-gray-500">
                            {formatCurrency(reportData.comparison.net_profit_after_tax)}
                          </td>
                          <td className="px-6 py-4 text-base text-right">
                            {renderVariance(reportData.variance.net_profit)}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td className="px-6 py-2 text-xs text-gray-500 italic pl-10">
                        {reportData.current.net_profit_after_tax >= 0
                          ? 'Net Profit Margin'
                          : 'Net Loss Margin'}
                        : {reportData.current.net_profit_margin.toFixed(2)}%
                      </td>
                      <td colSpan={reportData.comparison ? 3 : 1}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Report Footer */}
            <div className="mt-6 text-sm text-gray-500 print:block">
              <p>Generated on {new Date().toLocaleString('en-NG')}</p>
              <p>Report run by: {user?.email}</p>
              <p className="text-emerald-600 font-medium">Wholesale Module Report</p>
            </div>
          </>
        )}

        {/* Print Styles */}
        <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .p-4, .p-4 * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default WholesaleIncomeStatementPage;
